import { ReactNode, useContext, useState } from 'react';
import {
  Card,
  SelectTabData,
  SelectTabEvent,
  Tab,
  TabList,
  TabValue,
  Toolbar,
  makeStyles,
  tokens,
} from '@fluentui/react-components';

import DocumentContext from '../../contexts/DocumentContext.context';
import DocumentControlsLock from '../DocumentControlsLock';
import TableNodeTabContent from '@modules/documents/tiptap/nodes/table/TableNodeTabContent';
import Home from './Home';
import { ETiptapSelectorKey } from '../../types/tiptap.types';
import Insert from './Insert';
import getDocumentConfig from '../../helpers/getDocumentConfig.helper';
import isBlockDocument from '../../helpers/isBlockDocument.helper';
import SelectVersionDialog from '../../../items/components/dialogs/SelectVersionDialog';
import SelectHeadingDialog from '../dialogs/SelectHeadingDialog';
import SelectCaptionDialog from '../dialogs/SelectCaptionDialog';
import InsertLiteratureCitationDialog from '../dialogs/InsertLiteratureCitationDialog';
import { OptionsDialog } from '@shared/components';
import { TOptionsDialogState } from '@shared/types';
import { layoutTransition } from '@shared/constants/layoutTransition.constants';

import {
  EHeadingSelectionLabel,
  TBlockItemType,
  TSelectCaptionDialogState,
  TSelectHeadingDialogState,
} from '../../types';

import { SingleFieldDialog, TSingleFieldDialogState } from '@shared/components';
import {
  SelectItemDialog,
  TItem,
  TFolder,
  TRootItems,
  TSelectItemDialogState,
  TSelectVersionDialogState,
} from '@modules/items';
import { useItemScreenStore } from '@modules/items';
import { AccessType } from '@modules/access';
import { TERMINOLOGY } from '@shared/i18n';
import { TInsertLiteratureCitationDialogState, TSelectBlockDialogState } from '../../types/internal.types';
import useEditorActive from '../../hooks/useEditorActive.hook';
import ImageNodeTabContent from '../../tiptap/nodes/image/ImageNodeTabContent';
import { isTFolder } from '@modules/items/types/folders.types';
import { SEARCH_BLOCKS_CONDITION } from '@modules/search/constants';

const useStyles = makeStyles({
  primaryColor: {
    '& .fui-Tab__content, .fui-Tab__content:hover': {
      color: tokens.colorBrandForeground1,
    },
  },
});

type TProps = {
  selectedTab: TabValue;
  setSelectedTab: (value: TabValue) => void;
  // Tabs of the page around the editor, placed after Insert.
  pageTabs?: ReactNode;
  // Buttons and labels on the right of the tab row.
  actions?: ReactNode;
  // `card` puts the tab row in the toolbar's card, for a page that embeds the editor.
  appearance?: 'card';
};
const DocumentRibbon = ({ selectedTab, setSelectedTab, pageTabs, actions, appearance }: TProps) => {
  const { doc, version, isEdit, hasDraft } = useContext(DocumentContext);
  const { viewChanges } = useItemScreenStore();

  // Get content width for minWidth (header fills parent container via w-full)
  const { contentWidth } = getDocumentConfig(version?.config);

  const onTabSelect = (_: SelectTabEvent, data: SelectTabData) => {
    if (data.value === 'file') return;
    setSelectedTab(data.value);
  };

  // insert reference dialog:
  const [singleFieldDialogState, setSingleFieldDialogState] = useState<TSingleFieldDialogState<'input'> | undefined>(
    undefined
  );
  const [selectItemDialogState, setSelectItemDialogState] = useState<TSelectItemDialogState | undefined>(undefined);
  const [selectBlockDialogState, setSelectBlockDialogState] = useState<TSelectBlockDialogState | undefined>(undefined);
  const [selectVersionDialogState, setSelectVersionDialogState] = useState<TSelectVersionDialogState | undefined>(
    undefined
  );
  const [selectHeadingDialogState, setSelectHeadingDialogState] = useState<TSelectHeadingDialogState | undefined>(
    undefined
  );
  const [selectCaptionDialogState, setSelectCaptionDialogState] = useState<TSelectCaptionDialogState | undefined>(
    undefined
  );
  const [optionsDialogState, setOptionsDialogState] = useState<TOptionsDialogState | undefined>(undefined);
  const [selectBlockTargetDialogState, setSelectBlockTargetDialogState] = useState<TSelectItemDialogState | undefined>(
    undefined
  );
  const [insertLiteratureCitationDialogState, setInsertLiteratureCitationDialogState] = useState<
    TInsertLiteratureCitationDialogState | undefined
  >(undefined);

  const [selectionKind, setSelectionKind] = useState<TBlockItemType | null>(null);

  // They edit the selected table or image, so they come only with editing.
  const showTable = useEditorActive((editor) => editor.isActive(ETiptapSelectorKey.TABLE)) && isEdit;
  selectedTab = !showTable && selectedTab === 'table' ? 'home' : selectedTab;

  const showImage = useEditorActive((editor) => editor.isActive(ETiptapSelectorKey.IMAGE)) && isEdit;
  selectedTab = !showImage && selectedTab === 'image' ? 'home' : selectedTab;

  const initialFolderId = doc.resolvedPath[doc.resolvedPath.length - 1]._id;
  const blockSearchFallbackFolderId = selectBlockDialogState?.blocksFolderId;
  const getReferenceDisabledReason = (item: TItem) => {
    if (isBlockDocument(item))
      return `${TERMINOLOGY.block.upper} cannot be inserted as a ${TERMINOLOGY.reference.lower}.`;
    return ['REPLICA_FILE', 'REPLICA_DOCUMENT'].includes(item.type)
      ? `${TERMINOLOGY.reference.upperPlural} for ${TERMINOLOGY.replica.lowerPlural} are deactivated in your organization.`
      : undefined;
  };
  const blockTargetType = selectionKind === 'INLINE_BLOCK' ? 'INLINE_BLOCK' : 'DOCUMENT';
  const blockTargetLabel = selectionKind === 'INLINE_BLOCK' ? TERMINOLOGY.inlineBlock : TERMINOLOGY.block;
  const getBlockTargetDisabledReason = (item: TItem) => {
    if (item.type !== blockTargetType) return undefined;
    if (!item.hasDraft) return `This ${blockTargetLabel.lower} has no open draft.`;
    if (item.type === 'DOCUMENT' && item.hasDefaultVariant === false)
      return `Create a default variant of "${item.name}" before updating it.`;
    if (item.accessRight < AccessType.EDIT) return `You don't have edit rights for this ${blockTargetLabel.lower}.`;
    if (item._id === doc._id) return `The selection comes from this ${blockTargetLabel.lower}.`;
    return undefined;
  };
  const getBlockSearchFolderId = (data: TFolder | TRootItems) => {
    if (!isTFolder(data)) return undefined;
    return data.blocksFolder?._id ?? blockSearchFallbackFolderId;
  };
  const getBlockSearchPlaceholder = (data?: TFolder | TRootItems) => {
    if (data && !isTFolder(data)) return 'Search all libraries';

    const searchFolderId = data ? getBlockSearchFolderId(data) : blockSearchFallbackFolderId;
    if (searchFolderId === doc.insertBlocks.productBlocksRoot) return `Search ${TERMINOLOGY.productDatabase.lower}`;
    if (searchFolderId === doc.insertBlocks.globalBlocksRoot) return `Search ${TERMINOLOGY.qmDatabase.lower}`;
    return `Search ${TERMINOLOGY.blockEither.lower}`;
  };

  const toolbarDisabledReason = !isEdit
    ? hasDraft
      ? 'Edit draft to edit this document.'
      : 'Create draft to edit this document.'
    : viewChanges
      ? 'Close change view.'
      : undefined;

  const showToolbar = ['home', 'insert', 'table', 'image'].includes(selectedTab as string);
  const classes = useStyles();
  const tabRow = (
    <div className="flex pb-2 justify-between items-end">
      <TabList selectedValue={selectedTab} onTabSelect={onTabSelect} size="small">
        <Tab value="home">Home</Tab>
        <Tab value="insert">Insert</Tab>
        {pageTabs}
        {showTable && (
          <Tab value="table" className={classes.primaryColor}>
            <span className="text-primary hover:text-primary font-bold">Table</span>
          </Tab>
        )}
        {showImage && (
          <Tab value="image" className={classes.primaryColor}>
            <span className="text-primary hover:text-primary font-bold">Image</span>
          </Tab>
        )}
      </TabList>

      <div className="flex space-x-2 items-center">{actions}</div>
    </div>
  );
  const toolbar = (
    <DocumentControlsLock disabledReason={toolbarDisabledReason}>
      <Toolbar aria-label="Overflow" size="small" className="flex">
        {selectedTab === 'home' && <Home />}
        {selectedTab === 'insert' && (
          <Insert
            setSelectItemDialogState={setSelectItemDialogState}
            setSelectVersionDialogState={setSelectVersionDialogState}
            setSelectHeadingDialogState={setSelectHeadingDialogState}
            setSelectCaptionDialogState={setSelectCaptionDialogState}
            setOptionsDialogState={setOptionsDialogState}
            setSelectBlockDialogState={setSelectBlockDialogState}
            setSelectBlockTargetDialogState={setSelectBlockTargetDialogState}
            setSingleFieldDialogState={setSingleFieldDialogState}
            setInsertLiteratureCitationDialogState={setInsertLiteratureCitationDialogState}
            setSelectionKind={setSelectionKind}
          />
        )}
        {selectedTab === 'table' && <TableNodeTabContent />}
        {selectedTab === 'image' && <ImageNodeTabContent />}
      </Toolbar>
    </DocumentControlsLock>
  );

  return (
    <header
      className="sticky z-40 w-full"
      style={{ minWidth: contentWidth, transition: layoutTransition('min-width') }}
    >
      <SingleFieldDialog state={singleFieldDialogState} setState={setSingleFieldDialogState} />
      {!doc.isBlock && (
        <SelectItemDialog
          initialFolderId={initialFolderId}
          initialSelectedItems={[doc]}
          state={selectItemDialogState}
          setState={setSelectItemDialogState}
          title="Select file to reference"
          navigatableFolderTypes={['NORMAL', 'COUNTRY', 'SNAPSHOT']}
          selectableTypes={['DOCUMENT', 'FILE', 'REPLICA_DOCUMENT', 'REPLICA_FILE']}
          actionText="Insert"
          getDisabledReason={getReferenceDisabledReason}
          enableSearch
          searchOptions={{
            itemTypes: ['DOCUMENT', 'FILE'],
            blocksCondition: SEARCH_BLOCKS_CONDITION.NO,
            placeholder: `Search ${TERMINOLOGY.reference.lowerPlural}`,
          }}
        />
      )}
      {!doc.isBlock && blockSearchFallbackFolderId && (
        <SelectItemDialog
          initialFolderId={blockSearchFallbackFolderId}
          state={selectBlockDialogState}
          setState={setSelectBlockDialogState}
          title={`Select ${TERMINOLOGY.blockEither.lower} to insert`}
          navigatableFolderTypes={['BLOCK']}
          selectableTypes={['DOCUMENT', 'INLINE_BLOCK']}
          actionText="Insert"
          enableSearch
          searchOptions={{
            itemTypes: ['DOCUMENT', 'INLINE_BLOCK'],
            blocksCondition: SEARCH_BLOCKS_CONDITION.EXCLUSIVELY,
            placeholder: getBlockSearchPlaceholder,
            getFolderId: getBlockSearchFolderId,
          }}
        />
      )}
      <SelectItemDialog
        initialFolderId={doc.blocksRoot ?? doc.insertBlocks.globalBlocksRoot}
        state={selectBlockTargetDialogState}
        setState={setSelectBlockTargetDialogState}
        title={`Save selection as ${blockTargetLabel.lower}`}
        navigatableFolderTypes={['BLOCK']}
        selectableTypes={[blockTargetType]}
        actionText="Update"
        getDisabledReason={getBlockTargetDisabledReason}
        enableSearch
        searchOptions={{
          itemTypes: [blockTargetType],
          blocksCondition: SEARCH_BLOCKS_CONDITION.EXCLUSIVELY,
          placeholder: `Search ${blockTargetLabel.lower}`,
        }}
      />
      <SelectVersionDialog state={selectVersionDialogState} setState={setSelectVersionDialogState} />
      <SelectHeadingDialog
        state={selectHeadingDialogState}
        setState={setSelectHeadingDialogState}
        multiSelect={false}
        label={EHeadingSelectionLabel.REFERENCE}
      />
      <SelectCaptionDialog state={selectCaptionDialogState} setState={setSelectCaptionDialogState} />
      <OptionsDialog state={optionsDialogState} setState={setOptionsDialogState} />
      <InsertLiteratureCitationDialog
        state={insertLiteratureCitationDialogState}
        setState={setInsertLiteratureCitationDialogState}
      />

      {appearance === 'card' ? (
        <Card size="large" style={{ padding: '4px' }}>
          <div>
            {tabRow}
            {showToolbar && toolbar}
          </div>
        </Card>
      ) : (
        <div>
          {tabRow}
          {showToolbar && (
            <Card size="large" style={{ padding: '4px' }}>
              {toolbar}
            </Card>
          )}
        </div>
      )}
    </header>
  );
};

export default DocumentRibbon;
